import _ from 'underscore';
import React from 'react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AddressUtils } from '@bingads-webui-clientcenter/address';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { Button } from '@bingads-webui-react/primitive-with-signal';
import { AddressSuggestions } from './address-suggestions';
import { getAblEnforcementFormStyle } from './css/styles';
import type { AblEnforcementProps, AblEnforcementState, AddressSelectionState } from './types';

const { CountryCode } = Constants;

export class UnthemedAblEnforcement extends React.Component<AblEnforcementProps, AblEnforcementState> {
  constructor(props: AblEnforcementProps) {
    super(props);
    const hasAddressSuggestions = !_.isEmpty(this.props.addressSuggestions);
    if (hasAddressSuggestions) {
      this.state = {
        applyButtonEnabled: false,
      };
    } else {
      // Note: If there are no address suggestions, we would take the original address as the selected address.
      this.state = {
        applyButtonEnabled: true,
        addressSelected: props.originalAddress,
      };
      props.onAddressSelectionChange(this.state);
    }
  }

  onAddressSelectionChange = (state: AddressSelectionState) => {
    this.setState({
      applyButtonEnabled: true,
      addressSelected: state.addressSelected,
    }, () => this.props.onAddressSelectionChange(this.state));
  };

  renderMidContents = (hasAddressSuggestions: boolean, allowSaveInvalidAddress: boolean) => {
    if (hasAddressSuggestions && allowSaveInvalidAddress) {
      return (
        <div className={`abl-suggestions ${this.props.classes.ablSuggestions}`}>
          <div className="original-address">
            <strong>{this.props.i18n.getString(_TL_('What you entered:'))}</strong>
            {` ${AddressUtils.extendAddress(this.props.originalAddress).inlineFormat()}`}
          </div>
          <div className={`suggestion-list ${this.props.classes.suggestionList}`}>
            <strong>{this.props.i18n.getString(_TL_('Did you mean...?'))}</strong>
            <AddressSuggestions
              id={`${this.props.id}AddressSuggestions`}
              i18n={this.props.i18n}
              addressSuggestions={this.props.addressSuggestions}
              originalAddress={this.props.originalAddress}
              allowSaveInvalidAddress={allowSaveInvalidAddress}
              onAddressChange={this.onAddressSelectionChange}
            />
          </div>
        </div>
      );
    } else if (hasAddressSuggestions) {
      return (
        <div className={`abl-suggestions ${this.props.classes.ablSuggestions}`}>
          <div className={`suggestion-list ${this.props.classes.suggestionList}`}>
            <AddressSuggestions
              id={`${this.props.id}AddressSuggestions`}
              i18n={this.props.i18n}
              addressSuggestions={this.props.addressSuggestions}
              originalAddress={this.props.originalAddress}
              allowSaveInvalidAddress={allowSaveInvalidAddress}
              onAddressChange={this.onAddressSelectionChange}
            />
          </div>
        </div>
      );
    } else if (allowSaveInvalidAddress) {
      return (
        <div className={`abl-suggestions ${this.props.classes.ablSuggestions}`}>
          <div className="original-address">
            <strong>{this.props.i18n.getString(_TL_('What you entered:'))}</strong>
            {` ${AddressUtils.extendAddress(this.props.originalAddress).inlineFormat()}`}
          </div>
        </div>
      );
    }
    return null;
  };

  renderButtons = (hasPrimaryBtn: boolean) => {
    if (hasPrimaryBtn) {
      return (
        <div className="abl-enforcement-action-buttons">
          <Button
            btnStyle="primary"
            name="abl enforcement apply"
            className="lightboxPrimary"
            onClick={this.props.onApplyAction}
            disabled={!this.state.applyButtonEnabled}
          >
            {this.props.i18n.getString(_TL_('Apply'))}
          </Button>
          <Button
            btnStyle="default"
            name="abl enforcement go back"
            className="lightboxDefault"
            onClick={this.props.onCancelAction}
          >
            {this.props.i18n.getString(_TL_('Go back'))}
          </Button>
        </div>);
    }

    return (
      <div className="abl-enforcement-action-buttons">
        <Button
          btnStyle="default"
          className="lightboxDefault"
          onClick={this.props.onCancelAction}
        >
          {this.props.i18n.getString(_TL_('Go back'))}
        </Button>
      </div>);
  };

  render() {
    const {
      i18n,
      id,
      classes,
      country,
    } = this.props;

    const hasAddressSuggestions = !_.isEmpty(this.props.addressSuggestions);
    // Brazil addresses should not be allowed to save invalid addresses for tax purposes.
    const allowSaveInvalidAddress = country !== CountryCode.BR;

    let mainMessageText = null;
    let subMessageText = null;
    let hasPrimaryBtn = true;

    if (hasAddressSuggestions && allowSaveInvalidAddress) {
      mainMessageText = i18n.getString(_TL_('We found a similar address to the one you entered. Choose one from the list.'));
      subMessageText = i18n.getString(_TL_('We cannot verify this address. If you apply this address, we may contact you to verify it.'));
    } else if (hasAddressSuggestions) {
      mainMessageText = i18n.getString(_TL_("We weren't able to verify your address. Choose an address below, or go back and edit your entry."));
    } else if (allowSaveInvalidAddress) {
      mainMessageText = i18n.getString(_TL_('We cannot verify this address'));
      subMessageText = i18n.getString(_TL_('If you apply this address, we may contact you to verify it. Otherwise, please go back to edit your address.'));
    } else {
      mainMessageText = i18n.getString(_TL_("We weren't able to verify your address. Go back and edit your entry."));
      hasPrimaryBtn = false;
    }

    if (hasAddressSuggestions) {
      return (
        <div id={id} className={`abl-enforcement-container ${classes.lightboxContainer}`}>
          <div className={`lightbox-header ${classes.lightboxHeader}`}>
            <h4 className={`lightbox-title ${classes.lightboxTitle}`}>
              {i18n.getString(_TL_('Verify your address'))}
            </h4>
          </div>
          <div className={`lightbox-body ${classes.lightboxBody}`}>
            <div className={`lightbox-main-message ${classes.lightboxMainMessage}`}>
              <p>{mainMessageText}</p>
            </div>
            <div className={`lightbox-mid-content ${classes.lightboxMidContent}`}>
              {this.renderMidContents(hasAddressSuggestions, allowSaveInvalidAddress)}
            </div>
            <div className={`lightbox-sub-message ${classes.lightboxSubMessage}`}>
              <p>{subMessageText}</p>
            </div>
          </div>
          <div className={`lightbox-footer ${classes.lightboxFooter}`}>
            {this.renderButtons(hasPrimaryBtn)}
          </div>

        </div>
      );
    }

    return (
      <div id={id} className={`abl-enforcement-container ${classes.lightboxContainer}`}>
        <div className={`lightbox-header ${classes.lightboxHeader}`}>
          <h4 className="lightbox-title">
            {i18n.getString(_TL_('Verify your address'))}
          </h4>
        </div>
        <div className={`lightbox-body ${classes.lightboxBody}`}>
          <div className={`lightbox-main-message ${classes.lightboxMainMessage}`}>
            <p>{mainMessageText}</p>
          </div>
          <div className={`lightbox-mid-content ${classes.lightboxMidContent}`}>
            {this.renderMidContents(hasAddressSuggestions, allowSaveInvalidAddress)}
          </div>
          <div className={`lightbox-sub-message ${classes.lightboxSubMessage}`}>
            <p>{subMessageText}</p>
          </div>
        </div>
        <div className={`lightbox-footer ${classes.lightboxFooter}`}>
          {this.renderButtons(hasPrimaryBtn)}
        </div>
      </div>
    );
  }
}

(UnthemedAblEnforcement as typeof UnthemedAblEnforcement & {
  defaultProps: Partial<AblEnforcementProps>;
}).defaultProps = {
  addressSuggestions: [],
  originalAddress: {},
};

export const AblEnforcement = withDefaultStyles(UnthemedAblEnforcement, getAblEnforcementFormStyle);
